import React from "react";
import { Icon, IconButton, Spinner, SpinnerSize } from "@fluentui/react";
import { FunctionCall } from "../../../../interfaces/copilot-chat.interfaces";
import QuerySnippet from "../../../../pages/QuerySnippet";

interface FunctionStepCardProps {
    functionCall: FunctionCall;
    index: number;
    isExpanded: boolean;
    onToggleExpand: () => void;
}

/**
 * Turn a raw tool result into a readable string. Tool output is typically an
 * MCP-style array (`[{"type":"text","text":"..."}]`) whose `text` is itself a
 * JSON payload; we unwrap it and pretty-print any embedded JSON so it can be
 * shown inline instead of only being downloadable.
 */
const formatToolOutput = (content?: string): string => {
    if (!content) return "";
    let text = content;
    try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed)) {
            text = parsed
                .map((p) => (p && typeof p.text === "string" ? p.text : JSON.stringify(p, null, 2)))
                .join("\n");
        } else if (parsed && typeof parsed === "object") {
            text = JSON.stringify(parsed, null, 2);
        } else {
            text = String(parsed);
        }
    } catch {
        text = content;
    }
    // If the unwrapped text is itself JSON, pretty-print it.
    try {
        text = JSON.stringify(JSON.parse(text), null, 2);
    } catch {
        // not JSON — leave as-is
    }
    return text;
};

const FunctionStepCard: React.FC<FunctionStepCardProps> = ({
    functionCall,
    index,
    isExpanded,
    onToggleExpand,
}) => {
    const [outputCopied, setOutputCopied] = React.useState(false);
    const isRunning = functionCall.status === "running";
    const iconColor = isRunning ? "#0078d4" : functionCall.isError ? "#d13438" : "#107c10";
    const glow = isRunning
        ? "0 0 6px rgba(0,120,212,0.5)"
        : functionCall.isError
            ? "0 0 6px rgba(209,52,56,0.5)"
            : "0 0 6px rgba(16,124,16,0.4)";
    const statusText = isRunning ? "Running…" : functionCall.isError ? "Failure" : "Success";

    const formattedOutput = formatToolOutput(functionCall.content);

    const handleCopyOutput = () => {
        if (!formattedOutput) return;
        navigator.clipboard.writeText(formattedOutput);
        setOutputCopied(true);
        setTimeout(() => setOutputCopied(false), 1500);
    };

    const handleDownloadContent = () => {
        if (!functionCall.content) return;

        try {
            // Try to pretty-print JSON
            const jsonObj =
                typeof functionCall.content === "string"
                    ? JSON.parse(functionCall.content)
                    : functionCall.content;
            const formattedJson = JSON.stringify(jsonObj, null, 2);

            const blob = new Blob([formattedJson], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${functionCall.name}_content.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            try {
                // If it's not valid JSON or JSON.stringify fails, just save as-is
                const blob = new Blob([functionCall.content], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${functionCall.name}_content_raw.json`;
                a.click();
                URL.revokeObjectURL(url);
            } catch (innerErr) {
                console.error("Failed to download content:", innerErr);
            }
        }
    };

    return (
        <div
            style={{
                background: "#fff",
                borderRadius: "14px",
                marginBottom: "10px",
                padding: "12px 14px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
        >
            {/* Function header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                }}
                onClick={onToggleExpand}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {isRunning ? (
                        <Spinner size={SpinnerSize.xSmall} />
                    ) : (
                        <span
                            style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor: iconColor,
                                boxShadow: glow,
                                display: "inline-block",
                            }}
                        />
                    )}
                    <div style={{ display: "flex", flexDirection: "column", marginLeft: 4 }}>
                        <strong style={{ fontSize: "14px" }}>
                            Step {index + 1} - {functionCall.name}
                        </strong>
                        <span
                            style={{
                                fontSize: "11px",
                                color: isRunning ? "#0078d4" : functionCall.isError ? "#d13438" : "#107c10",
                                fontWeight: 500,
                            }}
                        >
                            Status: {statusText}
                        </span>
                    </div>
                </div>
                <IconButton
                    iconProps={{ iconName: isExpanded ? "ChevronUp" : "ChevronDown" }}
                    ariaLabel="Expand arguments"
                />
            </div>

            {/* Function arguments */}
            {isExpanded && (
                <div
                    style={{
                        marginTop: "10px",
                        borderTop: "1px solid #f3f2f1",
                        paddingTop: "10px",
                    }}
                >
                    {functionCall.arguments && functionCall.arguments.length > 0 && (
                        <>
                            <div style={{ fontSize: "12px", fontWeight: 600, color: "#605e5c", marginBottom: "4px" }}>Input</div>
                            {functionCall.arguments.map((arg, aIndex) => (
                                <div key={aIndex} style={{ marginTop: "6px" }}>
                                    {arg.name.toLowerCase() === "query" ? (
                                        <QuerySnippet arg={arg} />
                                    ) : (
                                        <div
                                            style={{
                                                padding: "6px 8px",
                                                borderRadius: "6px",
                                                background: "#fafafa",
                                                marginBottom: "4px",
                                                border: "1px solid #eee",
                                                display: "flex",
                                                gap: "6px",
                                                alignItems: "center",
                                            }}
                                        >
                                            <strong style={{ color: "#333" }}>{arg.name}:</strong>
                                            <span
                                                style={{
                                                    color: "#555",
                                                    fontFamily: "\"Consolas\", \"Courier New\", monospace",
                                                }}
                                            >
                                                {arg.value}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </>
                    )}

                    {/* Tool output — shown inline (readable) instead of download-only */}
                    {functionCall.content && (
                        <div style={{ marginTop: functionCall.arguments && functionCall.arguments.length > 0 ? "10px" : 0 }}>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: "4px",
                                }}
                            >
                                <span style={{ fontSize: "12px", fontWeight: 600, color: functionCall.isError ? "#d13438" : "#605e5c" }}>
                                    {functionCall.isError ? "Error output" : "Output"}
                                </span>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <button
                                        onClick={handleCopyOutput}
                                        title={outputCopied ? "Copied!" : "Copy output"}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            fontSize: "12px",
                                            color: outputCopied ? "#107c10" : "#0078d4",
                                            padding: 0,
                                        }}
                                    >
                                        <Icon iconName={outputCopied ? "CheckMark" : "Copy"} style={{ fontSize: 13 }} />
                                        <span>{outputCopied ? "Copied" : "Copy"}</span>
                                    </button>
                                    <button
                                        onClick={handleDownloadContent}
                                        title="Download full content"
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            fontSize: "12px",
                                            color: "#0078d4",
                                            padding: 0,
                                        }}
                                    >
                                        <Icon iconName="Download" style={{ fontSize: 13 }} />
                                        <span>Download</span>
                                    </button>
                                </div>
                            </div>
                            <pre
                                style={{
                                    margin: 0,
                                    padding: "10px",
                                    background: functionCall.isError ? "#fff4f4" : "rgb(241, 241, 241)",
                                    border: functionCall.isError ? "1px solid #f1bfc8" : "1px solid rgb(221, 221, 221)",
                                    borderRadius: "8px",
                                    fontFamily: "\"Consolas\", \"Courier New\", monospace",
                                    fontSize: "12px",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    maxHeight: "260px",
                                    overflow: "auto",
                                    color: functionCall.isError ? "#a4262c" : "#333",
                                }}
                            >
                                {formattedOutput}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FunctionStepCard;
